import { useState } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  Alert,
  Button,
  IconButton,
  SettingsEmptyState,
  SettingsBooleanControl,
  SettingsRow,
} from '@terra/ui-design-system';
import {
  Delete01Icon,
  Download01Icon,
  FolderOpenIcon,
  GlobeIcon,
  InformationCircleIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useProxyStore } from '../store/proxy-store';
import { ProxySettingsGroup } from './settings/proxy-settings/ProxySettingsGroup';
import { useBrowserExtensions } from './settings/proxy-settings/useBrowserExtensions';
import { ProxyValueAddDialog } from './settings/proxy-settings/ProxyValueAddDialog';

export function BrowserTab() {
  const t = useTranslations('settings.proxyForm.browser');
  const tBrowser = useTranslations('proxy.browser');
  const extensionsEnabled = useProxyStore(
    (state) => state.settings.extensionsEnabled,
  );
  const stealthLaunchEnabled = useProxyStore(
    (state) => state.settings.stealthLaunchEnabled,
  );
  const updateSettings = useProxyStore((state) => state.updateSettings);
  const {
    hasApplication,
    extensions,
    loadingState,
    handleInstallFromFolder,
    handleInstallFromCrx,
    handleInstallFromWebStore,
    handleToggleExtension,
    handleUninstallExtension,
  } = useBrowserExtensions();
  const isInstalling = loadingState !== 'idle';
  const [isWebStoreDialogOpen, setIsWebStoreDialogOpen] = useState(false);

  if (!hasApplication) {
    return (
      <Alert.Root>
        <Alert.Icon asChild>
          <InformationCircleIcon />
        </Alert.Icon>
        <Alert.Content>
          <Alert.Title>{t('selectApplicationTitle')}</Alert.Title>
          <Alert.Description>
            {t('selectApplicationDescription')}
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
    );
  }

  return (
    <VStack gap={6} alignItems="stretch" w="full">
      <ProxySettingsGroup>
        <SettingsRow
          label={t('enabled')}
          description={t('enabledDescription')}
          tooltip={t('enabledTooltip')}
          control={
            <SettingsBooleanControl
              checked={extensionsEnabled}
              onCheckedChange={(details) =>
                updateSettings({ extensionsEnabled: details.checked })
              }
            />
          }
        />
        <SettingsRow
          label={tBrowser('stealthMode')}
          description={tBrowser('enableStealthLaunchHelper')}
          control={
            <SettingsBooleanControl
              checked={stealthLaunchEnabled}
              onCheckedChange={(details) =>
                updateSettings({ stealthLaunchEnabled: !!details.checked })
              }
            />
          }
        />
      </ProxySettingsGroup>

      <ProxySettingsGroup
        title={t('installGroup')}
        description={t('installGroupDescription')}
        action={
          <HStack gap={2}>
            <Button
              size="xs"
              variant="outline"
              onClick={handleInstallFromFolder}
              disabled={isInstalling}
              loading={loadingState === 'folder'}
            >
              <FolderOpenIcon size={14} />
              {t('fromFolder')}
            </Button>
            <Button
              size="xs"
              variant="outline"
              onClick={handleInstallFromCrx}
              disabled={isInstalling}
              loading={loadingState === 'crx'}
            >
              <Download01Icon size={14} />
              {t('fromCrx')}
            </Button>
          </HStack>
        }
      >
        <SettingsRow
          label={t('sources.webstore')}
          description={t('webStorePlaceholder')}
          control={
            <Button
              size="xs"
              variant="outline"
              onClick={() => setIsWebStoreDialogOpen(true)}
              disabled={isInstalling}
              loading={loadingState === 'webstore'}
            >
              <GlobeIcon size={14} />
              {t('install')}
            </Button>
          }
        />
      </ProxySettingsGroup>

      <ProxySettingsGroup
        title={t('installedGroup')}
        description={t('installedCount', { count: extensions.length })}
      >
        {extensions.length === 0 ? (
          <SettingsEmptyState>{t('empty')}</SettingsEmptyState>
        ) : (
          extensions.map((extension) => (
            <SettingsRow
              key={extension.id}
              label={`${extension.name} v${extension.version}`}
              description={
                extension.description
                  ? `${t(`sources.${extension.source}`)} · ${extension.description}`
                  : t(`sources.${extension.source}`)
              }
              control={
                <HStack gap={2}>
                  <SettingsBooleanControl
                    checked={extension.enabled}
                    onCheckedChange={(details) =>
                      handleToggleExtension({
                        extensionId: extension.id,
                        enabled: details.checked,
                      })
                    }
                  />
                  <IconButton
                    size="xs"
                    variant="ghost"
                    aria-label={tBrowser('uninstallExtension')}
                    onClick={() => handleUninstallExtension(extension.id)}
                  >
                    <Delete01Icon size={14} />
                  </IconButton>
                </HStack>
              }
            />
          ))
        )}
      </ProxySettingsGroup>

      <ProxyValueAddDialog
        open={isWebStoreDialogOpen}
        title={t('installGroup')}
        description={t('installGroupDescription')}
        label={t('sources.webstore')}
        placeholder={t('webStorePlaceholder')}
        submitText={t('install')}
        onAdd={(url) => {
          void handleInstallFromWebStore(url);
        }}
        onClose={() => setIsWebStoreDialogOpen(false)}
      />
    </VStack>
  );
}
